<template>
  <div>
    <div
      class="text-center"
      v-if="!dataLoaded"
    >
      <v-progress-circular
        class="mt-6"
        color="primary"
        :size="50"
        indeterminate
      />
    </div>

    <div
      class="mt-4"
      v-show="dataLoaded"
    >
      <v-row class="my-0">
        <v-col
          cols="12"
          class="py-0"
        >
          <v-card class="py-2 px-4 mb-1 base-card d-flex justify-end align-center shadow">
            <v-menu
              offset-y
              left
              v-model="reportMenu"
            >
              <template #activator="{ on }">
                <v-btn
                  class="action-btn elevation-0 rounded-lg"
                  :disabled="reportMenuDisabled"
                  :loading="reportLoading"
                  v-on="on"
                  outlined
                  small
                  height="32"
                  title="Отчеты"
                  color="primary"
                  :ripple="false"
                >
                  <span class="ml-2 btn-text">Отчеты</span>
                  <v-icon
                    class="ml-1 uikit-icon primary chevron-down size-24 delay-03"
                    :class="reportMenu ? 'rotate-180' : ''"
                  />
                </v-btn>
              </template>

              <v-list>
                <v-list-item
                  dense
                  :disabled="!isCorrectFiltersForReportForWork || this.reportLoading"
                  @click="getReportForWork"
                >
                  <v-list-item-title class="list-text">
                    Научное направление деятельности
                    <template v-if="!isCorrectFiltersForReportForWork">
                      {{ annualReportWarningMessage }}
                    </template>
                  </v-list-item-title>
                </v-list-item>

                <v-list-item
                  dense
                  :disabled="!isCorrectFiltersForAnnualReport || this.reportLoading"
                  @click="getAnnualReport"
                >
                  <v-list-item-title class="list-text">
                    Итоговый отчет
                    <template v-if="!isCorrectFiltersForAnnualReport">
                      (необходимо выбрать кафедру и год в параметрах фильтра)
                    </template>
                  </v-list-item-title>
                </v-list-item>
              </v-list>
            </v-menu>

            <v-btn
              class="ml-1 pl-0"
              title="Поиск"
              icon
              @click="showSearch"
            >
              <v-icon class="uikit-icon search" />
            </v-btn>
            <v-btn
              class="ml-1"
              title="Фильтр"
              icon
              @click="showFilter"
            >
              <v-icon class="uikit-icon filter size-20" />
            </v-btn>
          </v-card>
        </v-col>
      </v-row>

      <v-row
        class="mb-0"
        :class="isShowFilter || isShowSearch ? 'mt-3' : 'mt-0'"
      >
        <v-col
          cols="12"
          class="py-0"
        >
          <CommonWorkFilters
            page-name="efficiency"
            :show-search="isShowSearch"
            :show-filter="isShowFilter"
            :showFilterFields="showFilterFields"
            :excludeTypes="excludeTypes"
            use-department-employee-relate
            @setFilters="setFilters"
            @removeChips="setTableHeight"
            @multipleFilterChangeHeight="setTableHeight"
            @setFilterEmployeeName="setFilterEmployeeName"
          />
        </v-col>
      </v-row>

      <v-row class="my-0">
        <v-col
          cols="12"
          class="pt-0"
        >
          <v-card
            ref="mainCard"
            class="base-card pb-4 pl-4 shadow"
            :class="{
              'pr-4': !isVerticalScrollInTable,
              'pr-0': isVerticalScrollInTable
            }"
          >
            <v-data-table
              ref="table"
              class="base-table fixed-last-column shadow-for-last-column is-vertical-scroll elevation-0 mt-3"
              :class="{ 'shadow-last-column': isShadowForLastColumn }"
              :loading="tableLoading"
              :headers="tableHeaders"
              :items="commonWorkParent.list"
              loading-text="Загрузка"
              no-data-text="Нет данных"
              no-results-text="Не найдено"
              hide-default-footer
              fixed-header
              :items-per-page="-1"
              v-scroll.self="tableScroll"
            >
              <template v-slot:body.prepend="">
                <div
                  v-if="!tableLoading"
                  style="height: 1px"
                ></div>
              </template>

              <template v-slot:item.index="{ index }">
                {{ index + 1 }}
              </template>

              <template v-slot:item.authors="{ item }">
                {{ getAuthorsList(item.authors.list) }}
              </template>

              <template v-slot:item.actions="{ item }">
                <div class="table-icons">
                  <v-icon
                    class="uikit-icon eye show-btn"
                    title="Открыть"
                    @click="show(item)"
                  />
                  <v-icon
                    v-if="isAdmin"
                    class="uikit-icon edit-icon edit-btn"
                    title="Редактировать"
                    @click="edit(item)"
                  />
                  <v-icon
                    v-if="isAdmin"
                    class="uikit-icon trash remove-btn"
                    title="Удалить"
                    @click="remove(item)"
                  />
                </div>
              </template>

              <template slot="no-data">
                <div>Таблица пуста</div>
              </template>
            </v-data-table>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <ConfirmDialog
      :dialog="confirmDialog"
      :loading="removeLoading"
      message="Подтвердите удаление работы"
      @confirm="removeCommonWork"
      @closeConfirmModal="closeConfirmDialog"
    />
  </div>
</template>

<script>
import { commonWorkApi, xlsxApi } from '@/api'
import { CommonWorkParent } from '@/models'
import ConfirmDialog from '@/components/dialogs/Confirm'
import CommonWorkFilters from '@/components/Works/CommonWorkParent/CommonWorkFilters'
import { Filters } from '@/models'
import { cloneDeep, debounce } from 'lodash'
import { getWorkTypeNameById } from '../scripts'
import { shadowForLastColumn } from '@/mixins/shadowForLastColumn'
import { mapGetters } from 'vuex'
import { tableHeight } from '@/mixins/tableHeight'
import { isVerticalScrollInTable } from '@/mixins/isVerticalScrollInTable'

export default {
  name: 'CommonWorkParent',

  metaInfo: {
    title: 'Научные работы'
  },

  components: {
    CommonWorkFilters,
    ConfirmDialog
  },

  mixins: [shadowForLastColumn, tableHeight, isVerticalScrollInTable],

  created() {
    this.filters = new Filters(this.savedFilter('efficiency'))
    this.getCommonWorkParent()
  },

  data: () => ({
    filters: new Filters(),
    currentFilterEmployeeName: null,
    tableHeaders: [
      { text: '№', value: 'index', sortable: false, align: 'start' },
      { text: 'ФИО cотрудника', value: 'authors', sortable: false, align: 'start' },
      { text: 'Показатель эффективности', value: 'workType.name', sortable: false, align: 'start' },
      { text: 'Год', value: 'year.year', sortable: false, align: 'start' },
      { text: 'Наименование работы', value: 'workName', sortable: false, align: 'start' },
      { text: 'Наименование издания', value: 'magazine.name', sortable: false, align: 'start' },
      { text: 'Действия', value: 'actions', sortable: false, align: 'end', width: '120px' }
    ],

    showFilterFields: {
      isShowSearchString: false,
      isShowWorkTypeId: false,
      isShowStatusId: false,
      isShowYearId: true,
      isShowDepartmentList: true,
      isShowFIO: false,
      isShowEmployeeId: true,
      isShowPeriodId: true,
      isShowEfficiencyIndicator: true,
      isShowEffContract: true
    },

    commonWorkParent: new CommonWorkParent(),
    tableLoading: false,
    selectedItem: null,
    confirmDialog: false,
    isShowMode: false,
    saveBtnLoading: false,
    removeLoading: false,
    dataLoaded: false,
    isShowFilter: false,
    isShowSearch: false,
    reportMenu: false,
    reportMenuDisabled: false,
    reportLoading: false,
    excludeTypes: [3, 4],

    pageNumber: 0,
    lastPage: false,
    total: 0,
    errorLoad: false
  }),

  computed: {
    ...mapGetters('user', ['isAdmin']),
    ...mapGetters('filters', ['savedFilter']),

    isCorrectFiltersForReportForWork() {
      return this.filters.periodId && this.filters.departmentsId?.length === 1
    },

    isCorrectFiltersForAnnualReport() {
      return this.filters.yearId && this.filters.departmentsId?.length
    },

    annualReportWarningMessage() {
      if (this.filters.employeeId && this.filters.periodId && !this.filters.departmentsId?.length) {
        return '(необходимо выбрать кафедру)'
      }

      return this.filters.departmentsId?.length > 1
        ? '(должна быть выбрана только одна кафедра)'
        : '(необходимо выбрать период, а также кафедру или сотрудника в параметрах фильтра)'
    }
  },

  methods: {
    showSearch() {
      this.isShowSearch = !this.isShowSearch
      this.isShowFilter = false
      this.setTableHeight()
    },

    showFilter() {
      this.isShowFilter = !this.isShowFilter
      this.isShowSearch = false
      this.setTableHeight()
    },

    setFilterEmployeeName(employeeName) {
      this.currentFilterEmployeeName = employeeName
    },

    async getReportForWork() {
      try {
        this.reportLoading = true

        const body = {
          departmentsId: this.filters.departmentsId,
          employeeId: this.filters.employeeId,
          periodId: this.filters.periodId,
          yearId: this.filters.yearId
        }

        await xlsxApi.reportForWork(body, this.currentFilterEmployeeName)
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Ошибка генерации отчета')
      } finally {
        this.reportLoading = false
      }
    },

    async getAnnualReport() {
      try {
        this.reportLoading = true

        const body = {
          departmentsId: this.filters.departmentsId,
          employeeId: this.filters.employeeId,
          periodId: this.filters.periodId,
          yearId: this.filters.yearId
        }

        await xlsxApi.annualReport(body)
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Ошибка генерации отчета')
      } finally {
        this.reportLoading = false
      }
    },

    getFullEngName(author) {
      return `${author.lastNameEng || ''} ${author.firstNameEng || ''} ${author.middleNameEng || ''}`
    },

    getAuthorsList(authorList) {
      let authors = ''
      for (const author of authorList)
        authors +=
          (author.employee?.fullName ||
            author.nameRus ||
            this.getFullEngName(author) ||
            'Отсутствует информации об имени') + ', '
      authors = authors.slice(0, -2)
      return authors
    },

    async getCommonWorkParent() {
      if (this.errorLoad) return
      try {
        if (!this.lastPage) {
          this.tableLoading = true

          this.filters.setExcludedType(this.excludeTypes)
          this.filters.statusId = 3

          const { content, last, totalElements } = await commonWorkApi.findAllByFilters(
            this.pageNumber,
            this.filters.getForAPI()
          )
          if (this.pageNumber === 0) this.commonWorkParent.list = []
          this.lastPage = last
          this.total = totalElements
          this.pageNumber++
          content.length &&
            content.forEach(obj => {
              this.commonWorkParent.add(obj?.work.commonWork)
              const currentCommonWork = this.commonWorkParent.list.find(el => el.id === obj.work?.commonWork.id)
              if (currentCommonWork) currentCommonWork.magazine = obj.work?.magazine
            })
          this.checkHeightElementAndChangeIsVerticalScrollInTable()
          this.tableLoading = false
        }
      } catch (e) {
        console.log(e)
        if (e) {
          this.tableLoading = false
          this.errorLoad = true
          this.showErrorMessage(e, 'Не удалось получить список работ')
        } else this.tableLoading = true
      } finally {
        this.dataLoaded = true
      }
    },

    show(item) {
      if (item.workType.id === 19) {
        this.$router.push('/journal-editorail-boards/journals/show/' + item.workId)
      } else {
        this.$router.push('/works/' + getWorkTypeNameById(item.workType.id) + '/show/' + item.workId)
      }
    },

    edit(item) {
      if (item.workType.id === 19) {
        this.$router.push('/journal-editorail-boards/journals/edit/' + item.workId)
      } else {
        this.$router.push('/works/' + getWorkTypeNameById(item.workType.id) + '/edit/' + item.workId)
      }
    },

    remove(item) {
      this.selectedItem = item
      this.confirmDialog = true
    },

    async saveCommonWork(body) {
      try {
        this.saveBtnLoading = true
        const data = body.id ? await commonWorkApi.update(body) : await commonWorkApi.create(body)
        body.id ? this.commonWorkParent.update(data) : this.commonWorkParent.add(data)
        this.$toast.info('Успешно сохранено')
        this.closeDialog()
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Ошибка сохранения')
      } finally {
        this.saveBtnLoading = false
      }
    },

    async removeCommonWork() {
      try {
        this.removeLoading = true
        await commonWorkApi.remove(this.selectedItem.id)
        this.$toast.info('Успешно удалено')
        this.commonWorkParent.remove(this.selectedItem.id)

        this.closeConfirmDialog()

        this.pageNumber--
        this.lastPage = false
        this.total = 0
        this.getCommonWorkParent()
      } catch (e) {
        console.log(e)
        this.showErrorMessage(e, 'Не удалось удалить работу')
      } finally {
        this.removeLoading = false
      }
    },

    setFilters: debounce(async function (filters) {
      this.filters = cloneDeep(filters)

      this.pageNumber = 0
      this.lastPage = false
      this.total = 0
      await this.getCommonWorkParent()
    }, 500),

    closeDialog() {
      this.selectedItem = null
      this.isShowMode = false
    },

    closeConfirmDialog() {
      this.confirmDialog = false
      this.selectedItem = null
    },

    tableScroll(event) {
      const el = event.target
      if (el.scrollHeight - el.clientHeight - el.scrollTop <= 10 && !this.tableLoading) {
        this.getCommonWorkParent()
      }
    }
  },

  watch: {
    dataLoaded() {
      this.setTableHeight()
    }
  }
}
</script>

<style scoped></style>
